import React, {
    useReducer, useRef, useEffect, useCallback
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import Auth from 'utils/Authentication';
import actions from 'Actions';
import ProductActions from 'actions/ProductActions';

import Location from 'utils/Location';
import Iovation from 'utils/Iovation';
import UrlUtils from 'utils/Url';
import FormValidator from 'utils/FormValidator';
import dateUtils from 'utils/Date';
import communityUtils from 'utils/Community';
import questionAnswerApi from 'services/api/questionAndAnswer';
import questionsAnswersAnalytics from 'analytics/bindingMethods/pages/productPage/questionsAnswersBindings';
import localeUtils from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';

import {
    Text, Container, Link, Box, Grid, Button, Divider
} from 'components/ui';
import { breakpoints } from 'style/config';
import Textarea from 'components/Inputs/Textarea/Textarea';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import Loader from 'components/Loader/Loader';
import SubmitMessage from 'components/ProductPage/QuestionsAndAnswers/SubmitMessage';
import ModalGuidelines from 'components/ProductPage/QuestionsAndAnswers/ModalGuidelines';
import UI from 'utils/UI';
import { PostLoad } from 'constants/events';
import { usePDPConfig } from 'components/ProductPage/hooks/usePDPConfig';
import QuestionsAndAnswersEN from 'components/ProductPage/QuestionsAndAnswers/locales/QuestionsAndAnswers_en_US';
import QuestionsAndAnswersFR from 'components/ProductPage/QuestionsAndAnswers/locales/QuestionsAndAnswers_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(QuestionsAndAnswersEN, QuestionsAndAnswersFR);

const MAX_ANSWER_CHARS = 1000;

let initialLoadFired = false;

const initialState = {
    productId: '',
    questionId: '',
    questionText: null,
    submissionTime: '',
    nickName: '',
    isSephoraEmployee: false,
    isFreeProduct: false,
    deviceData: null,
    answerTextError: false,
    isXS: null,
    showGuidelines: false,
    acceptedTerms: false,
    showTermsError: false,
    answerBody: null,
    answerTextMessage: null,
    biAccountId: null,
    userNickname: '',
    profileId: '',
    requestSuccess: false,
    requestFailed: false
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT_PARAMS':
            return { ...state, productId: action.productId, questionId: action.questionId, isXS: action.isXS };
        case 'SET_QUESTION_DATA':
            return { ...state, questionText: action.questionText, submissionTime: action.submissionTime, nickName: action.nickName };
        case 'SET_USER_DATA':
            return { ...state, biAccountId: action.biAccountId, profileId: action.profileId, userNickname: action.userNickname };
        case 'SET_DEVICE_DATA':
            return { ...state, deviceData: action.deviceData };
        case 'SET_ANSWER_TEXT_ERROR':
            return { ...state, answerTextError: true };
        case 'CLEAR_ANSWER_TEXT_ERROR':
            return { ...state, answerTextError: false, answerBody: action.answerBody };
        case 'SET_ANSWER_TEXT_MESSAGE':
            return { ...state, answerTextMessage: action.value, answerTextError: false };
        case 'TOGGLE_EMPLOYEE':
            return { ...state, isSephoraEmployee: !state.isSephoraEmployee };
        case 'TOGGLE_FREE_PRODUCT':
            return { ...state, isFreeProduct: !state.isFreeProduct };
        case 'TOGGLE_GUIDELINES':
            return { ...state, showGuidelines: !state.showGuidelines };
        case 'SET_ACCEPTED_TERMS':
            return { ...state, acceptedTerms: action.value, showTermsError: false };
        case 'SET_TERMS_ERROR':
            return { ...state, showTermsError: true };
        case 'REQUEST_SUCCESS':
            return { ...state, requestSuccess: true };
        case 'REQUEST_FAILED':
            return { ...state, requestFailed: true };
        default:
            return state;
    }
};

const SubmitAnswerBody = () => {
    const [state, localDispatch] = useReducer(reducer, initialState);
    const reduxDispatch = useDispatch();
    const topContainerRef = useRef(null);
    const answerTextRef = useRef(null);

    const currentProduct = useSelector(state_ => state_.page.product, shallowEqual);
    const user = useSelector(state_ => state_.user, shallowEqual);
    const userRef = useRef(user);
    userRef.current = user;

    const { isCommunityServiceEnabled } = usePDPConfig();

    useEffect(() => {
        if (currentProduct?.currentSku?.actionFlags && !initialLoadFired) {
            questionsAnswersAnalytics.fireSubmitPageLoad(currentProduct, false);
            initialLoadFired = true;
        }
    }, [currentProduct]);

    useEffect(() => {
        const productId = UrlUtils.getParamsByName('productId')[0];
        const questionId = UrlUtils.getParamsByName('questionId')[0];
        const skuId = UrlUtils.getParamsByName('skuId')[0];

        reduxDispatch(ProductActions.fetchCurrentProduct(productId, skuId));

        localDispatch({
            type: 'INIT_PARAMS',
            productId,
            questionId,
            isXS: window.matchMedia(breakpoints.xsMax).matches
        });

        Auth.requireLoggedInAuthentication()
            .then(() => communityUtils.ensureUserIsReadyForSocialAction(communityUtils.PROVIDER_TYPES.bv))
            .then(() => {
                const currentUser = userRef.current;
                localDispatch({
                    type: 'SET_USER_DATA',
                    biAccountId: currentUser.beautyInsiderAccount?.biAccountId,
                    profileId: currentUser.profileId,
                    userNickname: currentUser.nickName
                });
            })
            .catch(() => {
                Location.setLocation(`/product/${productId}#QandA`);
            });

        Sephora.Util.onLastLoadEvent(window, [PostLoad], () => {
            Iovation.loadIovationScript();
            Iovation.getBlackboxString([], true).then(blackBoxString => {
                localDispatch({ type: 'SET_DEVICE_DATA', deviceData: blackBoxString });
            });
        });

        questionAnswerApi
            .getQuestionById(questionId, skuId)
            .then(data => {
                if (data.Results && data.Results.length) {
                    localDispatch({
                        type: 'SET_QUESTION_DATA',
                        questionText: data.Results[0].QuestionDetails,
                        submissionTime: data.Results[0].SubmissionTime,
                        nickName: data.Results[0].UserNickname
                    });
                }
            })
            .catch(errors => {
                console.log('errors', errors); // eslint-disable-line no-console
            });
    }, []);

    const redirectToPpage = useCallback(() => {
        Location.setLocation(`/product/${state.productId}#QandA`);
    }, [state.productId]);

    const validateAnswerText = useCallback(answerText => {
        if (FormValidator.isEmpty(answerText)) {
            localDispatch({ type: 'SET_ANSWER_TEXT_ERROR' });

            return getText('enterAnswer');
        } else {
            localDispatch({ type: 'CLEAR_ANSWER_TEXT_ERROR', answerBody: answerText });
        }

        return null;
    }, []);

    const handleAnswerTextChange = useCallback(answerText => {
        localDispatch({
            type: 'SET_ANSWER_TEXT_MESSAGE',
            value: answerText.length === MAX_ANSWER_CHARS ? getText('maxCharacter') : null
        });
    }, []);

    const validateForm = useCallback(() => {
        const fieldsForValidation = [answerTextRef.current];
        const errors = FormValidator.getErrors(fieldsForValidation);
        let formIsValid = true;

        if (!state.acceptedTerms) {
            formIsValid = false;
            localDispatch({ type: 'SET_TERMS_ERROR' });
        }

        if (errors.fields.length) {
            formIsValid = false;
            questionsAnswersAnalytics.fireSubmitLinkTrackingError(errors.messages);
            UI.scrollTo({ ref: topContainerRef });
        }

        return formIsValid;
    }, [state.acceptedTerms]);

    const submitAnswer = useCallback(
        e => {
            e.preventDefault();

            const {
                productId,
                questionId,
                isFreeProduct,
                isSephoraEmployee,
                questionText,
                biAccountId,
                userNickname,
                profileId,
                deviceData,
                answerBody
            } = state;

            const isUS = localeUtils.isUS();
            const locale = localeUtils.isCanada() ? (localeUtils.isFrench() ? 'fr-CA' : 'en-CA') : 'en-US';

            if (validateForm()) {
                questionAnswerApi
                    .submitAnswer(
                        productId,
                        questionId,
                        encodeURIComponent(answerTextRef.current.getValue()).replace(/%\d\w|\+/g, ' '),
                        deviceData,
                        isSephoraEmployee,
                        isFreeProduct,
                        isCommunityServiceEnabled,
                        locale,
                        biAccountId,
                        userNickname,
                        profileId
                    )
                    .then(() => {
                        questionsAnswersAnalytics.fireSubmitSuccessPageLoad(currentProduct, false);

                        localDispatch({ type: 'REQUEST_SUCCESS' });
                        UI.scrollTo({ ref: topContainerRef });

                        const AnswerBodyData = {
                            answerBody: answerBody,
                            answerQuestionId: questionId,
                            answerQuestionText: questionText,
                            answerSkuId: currentProduct.currentSku?.skuId ? currentProduct.currentSku?.skuId : null,
                            answerProductId: currentProduct.productDetails?.productId,
                            answerPrice: currentProduct.currentSku?.listPrice,
                            answerCurrency: isUS ? 'USD' : 'CAD',
                            answerBrandName: currentProduct.productDetails?.brand?.displayName,
                            answerProductName: currentProduct.productDetails?.displayName,
                            answerVariationType: currentProduct.currentSku?.variationType,
                            answerVariationValue: currentProduct.currentSku?.variationValue,
                            answerProductTopCategory: currentProduct.parentCategory?.displayName
                        };
                        pdpAnalytics.trackEvent(
                            anaConsts.LINK_TRACKING_EVENT,
                            {
                                data: {
                                    linkName: anaConsts.EVENT_NAMES.ANSWER_QUESTION_BODY_DETAILS,
                                    actionInfo: anaConsts.EVENT_NAMES.ANSWER_QUESTION_BODY_DETAILS,
                                    specificEventName: anaConsts.EVENT_NAMES.ANSWER_QUESTION_BODY_DETAILS,
                                    sponsoredProductInformation: AnswerBodyData
                                }
                            },
                            { specificEventName: anaConsts.EVENT_NAMES.ANSWER_QUESTION_BODY_DETAILS }
                        );
                    })
                    .catch(error => {
                        if (error.responseStatus === 403 || error.responseStatus === 401 || error.responseStatus === 400) {
                            reduxDispatch(
                                actions.showSignInModal({
                                    isOpen: true,
                                    callback: () => submitAnswer(e),
                                    extraParams: { headerValue: '/api/bazaarvoice/answer' }
                                })
                            );
                        } else {
                            const errorMessages =
                                error && error.errorMessages && error.errorMessages.length
                                    ? error.errorMessages
                                    : ['Bazaarvoice returned error(s) during Answer Submit'];
                            questionsAnswersAnalytics.fireSubmitLinkTrackingError(errorMessages);
                            localDispatch({ type: 'REQUEST_FAILED' });
                            UI.scrollTo({ ref: topContainerRef });
                        }
                    });
            }
        },
        [state, currentProduct, isCommunityServiceEnabled, validateForm, reduxDispatch]
    );

    const {
        questionText, submissionTime, nickName, requestSuccess, requestFailed, showTermsError
    } = state;
    const currentSku = currentProduct?.currentSku;

    return (
        <Container
            paddingTop={[4, 6]}
            ref={topContainerRef}
        >
            {!requestSuccess && !requestFailed && (
                <form
                    noValidate
                    onSubmit={submitAnswer}
                >
                    <Grid
                        marginBottom={[4, 5]}
                        gap={3}
                        columns='1fr auto'
                        alignItems='baseline'
                        lineHeight='tight'
                    >
                        <Text
                            is='h1'
                            fontSize={['md', 'lg']}
                            fontWeight='bold'
                            children={getText('answerAQuestionTitle')}
                        />
                        <Link
                            display={[null, 'none']}
                            onClick={redirectToPpage}
                            color='blue'
                            padding={2}
                            margin={-2}
                            data-at={Sephora.debug.dataAt('cancel_link')}
                            children={getText('cancel')}
                        />
                    </Grid>
                    {questionText && (
                        <React.Fragment>
                            <Grid
                                backgroundColor='nearWhite'
                                borderRadius={2}
                                paddingX={[3, 4]}
                                paddingY={[2, 3]}
                                gap={null}
                                columns='1.5em 1fr'
                                maxWidth={612}
                            >
                                <Text
                                    aria-label={getText('question')}
                                    fontWeight='bold'
                                    children='Q:'
                                />
                                <div>
                                    <Text
                                        is='p'
                                        fontWeight='bold'
                                        data-at={Sephora.debug.dataAt('asked_question_text')}
                                        children={questionText}
                                    />
                                    <Text
                                        is='p'
                                        marginTop='.125em'
                                        color='gray'
                                        fontSize={['sm', 'base']}
                                        data-at={Sephora.debug.dataAt('asked_question_date')}
                                        children={`${dateUtils.formatSocialDate(submissionTime, true)} ${
                                            nickName ? getText('postedBy', [nickName]) : ''
                                        }`}
                                    />
                                </div>
                            </Grid>
                            <Divider marginY={[5, 6]} />
                        </React.Fragment>
                    )}
                    <Box
                        maxWidth={612}
                        marginBottom={4}
                    >
                        <Textarea
                            label={getText('enterYourAnswer', [MAX_ANSWER_CHARS])}
                            labelDataAt={'answer_field_ghost_text'}
                            rows={state.isXS ? 5 : 4}
                            name='reviewAnswer'
                            maxLength={MAX_ANSWER_CHARS}
                            charCountDataAt={'char_counter_text'}
                            handleChange={handleAnswerTextChange}
                            invalid={state.answerTextError}
                            errorDataAt={'error_msg_text'}
                            warning={state.answerTextMessage}
                            validate={validateAnswerText}
                            ref={answerTextRef}
                            data-at={Sephora.debug.dataAt('answer_field')}
                        />
                        <Link
                            padding={2}
                            margin={-2}
                            onClick={() => localDispatch({ type: 'TOGGLE_GUIDELINES' })}
                            color='blue'
                            data-at={Sephora.debug.dataAt('guidelines_link')}
                            children={getText('questionAnswerGuidelines')}
                        />
                    </Box>
                    <Checkbox
                        paddingY={2}
                        checked={!!state.isSephoraEmployee}
                        data-at={Sephora.debug.dataAt('work_at_seph_checkbox')}
                        onClick={() => localDispatch({ type: 'TOGGLE_EMPLOYEE' })}
                    >
                        {getText('iWorkAtSephora')}
                    </Checkbox>
                    <Checkbox
                        paddingY={2}
                        checked={state.isFreeProduct}
                        data-at={Sephora.debug.dataAt('free_samples_checkbox')}
                        onClick={() => localDispatch({ type: 'TOGGLE_FREE_PRODUCT' })}
                    >
                        {getText('iReceivedFreeSample')}
                    </Checkbox>
                    <Divider
                        marginTop={4}
                        marginBottom={4}
                    />
                    <Checkbox
                        marginTop={4}
                        marginBottom={2}
                        paddingY={null}
                        checked={state.acceptedTerms}
                        onClick={e => localDispatch({ type: 'SET_ACCEPTED_TERMS', value: e.target.checked })}
                        color={showTermsError ? 'error' : null}
                    >
                        {getText('yesIAgree')}
                        <Link
                            href='https://www.sephora.com/beauty/terms-of-use'
                            color='blue'
                            data-at={Sephora.debug.dataAt('terms_and_conditions_link')}
                            padding={2}
                            margin={-2}
                            target='_blank'
                        >
                            {getText('termsAndConditions')}
                        </Link>
                        .
                    </Checkbox>
                    <Grid
                        gap={3}
                        maxWidth={612}
                    >
                        {showTermsError && (
                            <Text
                                is='p'
                                color='error'
                                paddingBottom={2}
                                paddingLeft={6}
                                fontSize='sm'
                                lineHeight='tight'
                            >
                                {getText('termsError')}
                            </Text>
                        )}
                        <Text
                            fontSize='sm'
                            lineHeight='tight'
                            paddingLeft={6}
                            color='gray'
                        >
                            <Text
                                display='inline-block'
                                marginTop='.125em'
                                fontWeight='bold'
                            >
                                {getText('noteLabel')}:{' '}
                            </Text>
                            {getText('responsiblity')}
                        </Text>
                        <Text
                            is='p'
                            lineHeight='tight'
                            fontSize='sm'
                            color='gray'
                            children={getText('answerNote')}
                        />
                        <Grid
                            gap={4}
                            alignItems='center'
                            columns={[null, 'auto 1fr']}
                        >
                            <Button
                                width={['100%', 'auto']}
                                minWidth={[null, '14.5em']}
                                variant='primary'
                                type='submit'
                                data-at={Sephora.debug.dataAt('submit_answer_button')}
                                children={getText('submit')}
                            />
                            <Link
                                display={['none', 'block']}
                                color='blue'
                                padding={2}
                                margin={-2}
                                marginRight='auto'
                                onClick={redirectToPpage}
                                data-at={Sephora.debug.dataAt('cancel_link')}
                                children={getText('cancel')}
                            />
                        </Grid>
                    </Grid>
                </form>
            )}
            {!currentSku && <Loader isShown={true} />}
            {(requestSuccess || requestFailed) && (
                <SubmitMessage
                    redirectTo={redirectToPpage}
                    isError={requestFailed}
                />
            )}
            {state.showGuidelines && <ModalGuidelines onDismiss={() => localDispatch({ type: 'TOGGLE_GUIDELINES' })} />}
        </Container>
    );
};

export default wrapFunctionalComponent(SubmitAnswerBody, 'SubmitAnswerBody');
